import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "../lib/api";
import { Seo } from "../lib/seo";
import { Section, SectionHead, Button } from "../components/ui";

export default function Mcp() {
  const { data } = useQuery({ queryKey: ["mcp-info"], queryFn: () => get("/mcp-info") });
  const url = data?.connect_url || "/api/mcp";
  const tools = data?.tools || [];
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); };

  return (
    <>
      <Seo title="MCP Server" path="/developers/mcp/" description="Connect Claude Desktop to the BloomCONNECT MCP server with one click and let your assistant autonomously manage your n8n Cloud workflows, executions and webhooks." />
      <Section>
        <SectionHead label="MCP Server" title="Let Claude run your automation." intro="A one-click remote MCP connector. Add the URL to Claude Desktop and your assistant can configure and drive your n8n Cloud workspace autonomously." />

        <div className="mt-8 max-w-2xl">
          <div className="rounded-lg border border-line bg-surface-sunk p-4">
            <div className="eyebrow mb-2">Connector URL</div>
            <div className="flex items-center gap-3">
              <code className="font-mono text-sm text-ink-900 break-all flex-1" data-testid="mcp-public-url">{url}</code>
              <button onClick={copy} className="btn-primary btn-sm shrink-0" data-testid="copy-mcp-public-url">{copied ? "Copied" : "Copy"}</button>
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-2 items-start">
          <div className="max-w-prose text-ink-600 space-y-4">
            <h2 className="text-xl text-ink-950">Connect in Claude Desktop</h2>
            <ol className="space-y-2 list-decimal pl-5">
              <li>Open <span className="text-ink-900">Settings → Connectors → Add custom connector</span>.</li>
              <li>Paste the connector URL above and save.</li>
              <li>Open <span className="text-ink-900">Request headers</span> and set <code className="font-mono text-ink-900">Authorization</code> to <code className="font-mono text-ink-900">Bearer &lt;your token&gt;</code>. Generate the token in your BloomCONNECT admin dashboard (Integrations → Connector tokens).</li>
              <li>Enable the connector in a conversation.</li>
              <li>Ask Claude to run <code className="font-mono text-ink-900">configure_n8n</code> with your n8n Cloud URL and API key.</li>
              <li>Claude can now list, create, update, activate and trigger your workflows and read executions.</li>
            </ol>
            <p className="text-sm text-ink-500">The endpoint is authenticated — a valid connector token is required. Tokens are hashed at rest, shown once, and revocable. Your n8n key is validated, encrypted at rest and never exposed to the browser or logs.</p>
          </div>

          <div className="card-hairline p-6">
            <div className="eyebrow mb-3">Available tools ({tools.length})</div>
            <ul className="space-y-3">
              {tools.map((t) => (
                <li key={t.name} className="border-b border-line pb-3 last:border-0 last:pb-0">
                  <code className="font-mono text-sm text-brand-700">{t.name}</code>
                  <p className="text-xs text-ink-500 mt-1">{t.description}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10"><Button to="/developers/" variant="secondary" size="md">See the REST API</Button></div>
      </Section>
    </>
  );
}
